import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import baseURL from '../../config';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Paper,
  TextField,
  InputAdornment,
  styled,
  TablePagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import bgImage from '../../assets/shipimage.jpg'; // Add the image import

// Styled components for modern table design
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  padding: theme.spacing(2),
  borderBottom: '1px solid #e0e0e0',
  color: '#555',
}));

const StyledTableHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 700,
  padding: theme.spacing(2),
  borderBottom: '2px solid #1976d2',  // Custom border color for header
  backgroundColor: '#1976d2',  // Blue color for header
  color: 'white',
  textTransform: 'uppercase',
  letterSpacing: '1px',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: '#fafafa',  // Light gray background for odd rows
  },
  '&:hover': {
    backgroundColor: '#f1f1f1',  // Light hover effect
    cursor: 'pointer',
  },
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
  borderRadius: '8px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
}));

const AssignedVessel = () => {
  const { id } = useParams();
  const [vessels, setVessels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredVessels, setFilteredVessels] = useState([]);
  const [page, setPage] = useState(0); // State to track the current page
  const [rowsPerPage, setRowsPerPage] = useState(5); // Number of rows per page

  useEffect(() => {
    const fetchAssignedVessels = async () => {
      try {
        const token = localStorage.getItem("token");

        const response = await axios.get(
          `${baseURL}/api/vesselOwner/getAssignedVesselsByVesselOwnerId/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        
        setVessels(response.data?.vessels || []);
      } catch (error) {
        console.error("Error fetching assigned vessels:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAssignedVessels();
  }, [id]);

  useEffect(() => {
    if (searchQuery) {
      const filtered = vessels.filter((vessel) => {
        const lowercasedQuery = searchQuery.toLowerCase();
        return (
          vessel.vesselname.toLowerCase().includes(lowercasedQuery) ||
          vessel.IMO_Number.toLowerCase().includes(lowercasedQuery) ||
          vessel.crewingAgentName?.toLowerCase().includes(lowercasedQuery)
        );
      });
      setFilteredVessels(filtered);
    } else {
      setFilteredVessels(vessels);
    }
  }, [searchQuery, vessels]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset page when rows per page changes
  };

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" height="100vh"><CircularProgress /></Box>;

  return (
    <Box
      sx={{
        p: 3,
        minHeight: '100vh',
        bgcolor: '#f4f4f9',
        backgroundImage: `url(${bgImage})`,  // Set the background image here
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
      }}
    >
      <Typography variant="h4" fontWeight="bold" textAlign="center" gutterBottom color="primary">
        Assigned Vessels
      </Typography>

      {/* Search Bar with Modern Design */}
      <SearchContainer>
        <TextField
          label="Search Vessels"
          variant="outlined"
          fullWidth
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by vessel name, IMO, or agent name"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            maxWidth: 600,
            '& .MuiOutlinedInput-root': {
              borderRadius: '20px',
            },
            '& .MuiInputLabel-outlined': {
              color: 'rgba(0, 0, 0, 0.6)',
            },
          }}
        />
      </SearchContainer>

      {filteredVessels.length > 0 ? (
        <TableContainer component={Paper} elevation={4} sx={{ borderRadius: 3, boxShadow: 3, overflow: 'hidden' }}>
          <Table sx={{ minWidth: 650 }} aria-label="assigned vessels table">
            <TableHead>
              <TableRow>
                <StyledTableHeaderCell>Vessel Name</StyledTableHeaderCell>
                <StyledTableHeaderCell align="left">IMO Number</StyledTableHeaderCell>
                <StyledTableHeaderCell align="left">Flag</StyledTableHeaderCell>
                <StyledTableHeaderCell align="left">Agent Name</StyledTableHeaderCell>
                <StyledTableHeaderCell align="left">GRT</StyledTableHeaderCell>
                <StyledTableHeaderCell align="left">BHP</StyledTableHeaderCell>
                <StyledTableHeaderCell align="left">Call Sign</StyledTableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredVessels.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((vessel) => (
                <StyledTableRow key={vessel._id}>
                  <StyledTableCell component="th" scope="row">
                    {vessel.vesselname}
                  </StyledTableCell>
                  <StyledTableCell align="left">{vessel.IMO_Number}</StyledTableCell>
                  <StyledTableCell align="left">{vessel.Flag}</StyledTableCell>
                  <StyledTableCell align="left">{vessel.crewingAgentName || 'N/A'}</StyledTableCell>
                  <StyledTableCell align="left">{vessel.grt || 'N/A'}</StyledTableCell>
                  <StyledTableCell align="left">{vessel.bhp || 'N/A'}</StyledTableCell>
                  <StyledTableCell align="left">{vessel.call_sign || 'N/A'}</StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
          {/* Pagination Component Inside Table */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredVessels.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      ) : (
        <Paper elevation={3} sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
          <Typography variant="h6" color="textSecondary">No vessels assigned.</Typography>
        </Paper>
      )}
    </Box>
  );
};

export default AssignedVessel;
